import { ouvrirFluxSse } from '../services/fluxSse';

/**
 * Flux temps réel du chauffeur, et permission de notification système.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────
 * PWA-01 — deux défauts corrigés ici, tous deux structurels.
 *
 * 1. Le SSE dépendait de la permission de notification. `Dashboard` (supprimé le 2026-09-20) demandait la permission au
 *    montage, puis n'ouvrait le flux QUE si elle avait été accordée. Un chauffeur qui refusait
 *    les notifications perdait donc aussi le temps réel — conséquence que rien n'annonçait et
 *    que rien ne rattrapait. Les deux mécanismes sont désormais indépendants : le flux s'ouvre
 *    quelle que soit la permission, et `Notification.permission` ne le conditionne plus nulle
 *    part. C'est ce que vérifie la mutation D du chantier.
 *
 * 2. Le SSE affichait lui-même des notifications système via `new Notification()`. Avec l'arrivée
 *    du Web Push, le même événement en aurait produit deux : une par le flux, une par le Service
 *    Worker. La répartition est maintenant nette —
 *
 *        SSE      → met à jour l'interface (cette fonction)
 *        Web Push → produit la notification système (Service Worker)
 *
 *    et `new Notification()` a disparu de ce module. Le Push atteint le chauffeur même PWA
 *    fermée ; le SSE, qui exige une page ouverte, n'a aucune raison de doublonner.
 * ────────────────────────────────────────────────────────────────────────────────────────────
 *
 * Le mécanisme SEC-07 est intégralement conservé : `fetch` + `ReadableStream`, jeton dans
 * `Authorization` et jamais dans l'URL, rafraîchissement Keycloak mutualisé, reconnexion à
 * back-off exponentiel, abandon après un nombre maximal de tentatives.
 */

/** Demande la permission d'afficher des notifications système. */
export const requestNotificationPermission = async (): Promise<NotificationPermission> => {
    if (!('Notification' in window)) {
        return 'denied';
    }
    if (Notification.permission !== 'default') {
        // Déjà tranché — accordé ou refusé. Redemander est sans effet et, sur un refus,
        // le navigateur ne réaffichera de toute façon plus rien.
        return Notification.permission;
    }
    return Notification.requestPermission();
};

/** Permission courante, sans jamais déclencher de demande. */
export const permissionNotificationCourante = (): NotificationPermission => {
    if (!('Notification' in window)) {
        return 'denied';
    }
    return Notification.permission;
};

export type TypeEvenementMission = 'notification-created' | 'notification-status-changed';

export interface EvenementMission {
    type: TypeEvenementMission;
    /** Charge brute de l'événement SSE, telle que le backend la sérialise. */
    donnees: any;
}

/**
 * Ouvre le flux temps réel des missions du chauffeur.
 *
 * @param chauffeurTelephone téléphone du chauffeur, utilisé dans le CHEMIN de l'URL. Le backend
 *                           vérifie qu'il correspond au `preferred_username` du jeton : ce n'est
 *                           donc pas une identité déclarée par le client, seulement une adresse
 *                           de flux que le serveur revalide.
 * @param surEvenement       appelé à chaque événement reçu. C'est par ici que l'interface se met
 *                           à jour. Aucune notification système n'est produite.
 * @returns fonction de fermeture du flux
 */
export const subscribeToNotifications = (
    chauffeurTelephone: string,
    surEvenement: (evenement: EvenementMission) => void = () => undefined,
    isActive: { current: boolean } = { current: true },
): (() => void) => {
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

    const relayer = (event: MessageEvent, type: TypeEvenementMission) => {
        if (!isActive.current) return;
        try {
            surEvenement({ type, donnees: JSON.parse(event.data) });
        } catch (e) {
            // Un événement illisible ne doit pas rompre le flux : les suivants restent utiles.
            console.error(`Événement ${type} illisible`, e);
        }
    };

    const flux = ouvrirFluxSse(
        () => `${API_URL}/notifications-chauffeur/stream/${chauffeurTelephone}`,
        {
            evenements: {
                'notification-created': (event) => relayer(event, 'notification-created'),
                'notification-status-changed': (event) => relayer(event, 'notification-status-changed'),
            },
            onAbandon: () => {
                console.warn('Temps réel interrompu : session expirée ou serveur injoignable.');
            },
        },
    );

    return () => {
        isActive.current = false;
        flux.fermer();
    };
};
